<template>
  <widget-submissions-layout
    :page_title="`${organization_label}: Guest Check-In Submissions`"
    heading="Guest Check-In Submissions"
    description="View and manage guest check-in submissions"
    search_label="Search by name, email, or phone"
    :search_text.sync="full_text"
    :loading="loading"
    @refresh="loadForms"
  >
    <template #menu-items>
      <v-subheader>EXPORT</v-subheader>
      <v-list-item @click="exportSubmissions">
        <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
        <v-list-item-title>Export Submissions</v-list-item-title>
      </v-list-item>
      <v-subheader>ANALYTICS & FORMS</v-subheader>
      <v-list-item
        :to="{
          name: 'search-forms-guest-check-in',
          params: { organization_id: $route.params.organization_id },
        }"
      >
        <v-list-item-icon><v-icon>mdi-form-select</v-icon></v-list-item-icon>
        <v-list-item-title>All Guest Check-In Forms</v-list-item-title>
      </v-list-item>
      <v-list-item
        :to="{
          name: 'guest-check-in-analytics',
          params: { organization_id: $route.params.organization_id },
        }"
      >
        <v-list-item-icon><v-icon>mdi-chart-line</v-icon></v-list-item-icon>
        <v-list-item-title>All Guest Check-In Analytics</v-list-item-title>
      </v-list-item>

      <v-subheader>HOW IT WORKS</v-subheader>

      <v-list-item @click="viewDemoGuestCheckInFlow">
        <v-list-item-icon><v-icon>mdi-presentation</v-icon></v-list-item-icon>
        <v-list-item-title>Demo - Guest Check-In Forms</v-list-item-title>
      </v-list-item>

      <div v-if="should_enable_generate_submissions">
        <v-divider></v-divider>
        <v-subheader>STAFF ACTIONS</v-subheader>
        <v-list-item @click="showGenerateSubmissionsDialog">
          <v-list-item-icon><v-icon>mdi-plus-circle-multiple</v-icon></v-list-item-icon>
          <v-list-item-title>Generate Test Submissions</v-list-item-title>
        </v-list-item>
      </div>
    </template>

    <template #filters>
      <v-select
        v-model="selected_form_id"
        :items="form_options"
        label="Filter by Form"
        item-text="text"
        item-value="value"
        clearable
        hide-details
        dense
        outlined
        @input="doUpdate"
      />

      <v-checkbox
        v-model="show_test_submissions"
        label="Show Test Submissions"
        @change="doUpdate"
        hide-details
        dense
        class="mt-0"
      ></v-checkbox>
    </template>

    <template #filter-actions>
      <v-btn-toggle
        v-model="filter_state"
        dense
        mandatory
      >
        <v-btn value="all">All</v-btn>
        <v-btn value="double-opt-in-pending">Pending</v-btn>
        <v-btn value="opted-in">Opted In</v-btn>
        <v-btn value="opted-out">Did Not Opt In</v-btn>
        <v-btn value="double-opt-in-not-used">Not Used</v-btn>
      </v-btn-toggle>
    </template>

    <template #table>
      <v-data-table
        v-if="results != null"
        :headers="headers"
        :items="results"
        :items-per-page="50"
        :loading="loading"
        class="enhanced-submissions-table elevation-1"
        :hide-default-footer="false"
        disable-sort
        :no-data-text="no_data_message"
        :footer-props="{
          itemsPerPageText: 'Submissions per page:',
          itemsPerPageOptions: [25, 50, 100],
        }"
        item-key="submission_id"
      >
        <template v-slot:item.is_test_submission="{ item }">
          <v-chip
            small
            :color="item.is_test_submission ? 'orange' : 'green'"
            :text-color="item.is_test_submission ? 'white' : 'white'"
            class="status-chip"
          >
            {{ item.is_test_submission ? 'Test' : 'Live' }}
          </v-chip>
        </template>
        <template v-slot:item.time_submit_date="{ item }">
          {{ $rc.pretty(item.time_submit, 'date') }}
        </template>
        <template v-slot:item.time_submit_time="{ item }">
          {{ $rc.pretty(item.time_submit, 'time') }}
        </template>
        <template v-slot:item.email_consent_agreed="{ item }">
          <v-icon v-if="item.email_consent_agreed" color="green" small>mdi-check-circle</v-icon>
          <v-icon v-else color="grey" small>mdi-close-circle</v-icon>
        </template>
        <template v-slot:item.endpoint="{ item }">
          {{ $rc.pretty(item.endpoint, 'endpoint_no_parens') }}
        </template>
        <template v-slot:item.phone_consent_agreed="{ item }">
          <v-icon v-if="item.phone_consent_agreed" color="green" small>mdi-check-circle</v-icon>
          <v-icon v-else color="grey" small>mdi-close-circle</v-icon>
        </template>
        <template v-slot:item.double_opt_in_state="{ item }">
          <v-chip small :color="getStateColor(item.double_opt_in_state)" dark class="status-chip">
            {{ getStateDisplayText(item.double_opt_in_state) }}
          </v-chip>
        </template>
        <template v-slot:item.time_double_opt_in_complete_date="{ item }">
          {{ $rc.pretty(item.time_double_opt_in_complete, 'date') }}
        </template>
        <template v-slot:item.time_double_opt_in_complete_time="{ item }">
          {{ $rc.pretty(item.time_double_opt_in_complete, 'time') }}
        </template>
      </v-data-table>
    </template>

    <template #footer-notes>
      <div class="mt-3 text-caption text--secondary">
        <strong>*</strong> Double Opt-In State — outcome based on this submission only
      </div>
    </template>

    <template #dialogs>
      <v-dialog v-model="generate_submissions_dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Generate Test Submissions</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="selected_forms_for_generation"
                  :items="form_options"
                  label="Guest Check-In Forms"
                  item-text="text"
                  item-value="value"
                  outlined
                  multiple
                  chips
                  deletable-chips
                  :rules="[rules.required]"
                  hint="Select one or more forms to generate submissions for"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="submission_count"
                  label="Number of Submissions"
                  type="number"
                  min="1"
                  max="50"
                  outlined
                  dense
                  :rules="[rules.required, rules.submission_count_range]"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="seed"
                  label="Seed (optional)"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hint="Use a seed to get reproducible test data"
                  persistent-hint
                />
              </v-col>

            </v-row>
          </v-container>
          <small class="text--secondary">
            This will generate test submissions with varying data, opt-in states, and double opt-in
            statuses. All submissions will use test phone numbers and emails. When multiple forms
            are selected, submissions will be distributed evenly across them. It will not mark them
            as test submissions so that you can see them on analytics if needed.
          </small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="generate_submissions_dialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="generateTestSubmissions"
            :loading="generating_submissions"
            :disabled="!selected_forms_for_generation.length || !submission_count"
          >
            Generate
          </v-btn>
        </v-card-actions>
      </v-card>
      </v-dialog>
    </template>
  </widget-submissions-layout>
</template>

<script>
import _ from 'lodash';
import WidgetSubmissionsLayout from '@/components/widget/widget-submissions-layout.vue';

export default {
  components: {
    WidgetSubmissionsLayout,
  },

  data() {
    return {
      loading: false,
      results: [],
      headers: [
        { text: 'Type', value: 'is_test_submission', align: 'center' },
        { text: 'Date', value: 'time_submit_date' },
        { text: 'Time', value: 'time_submit_time' },
        { text: 'Form Name', value: 'form_name' },
        { text: 'First Name', value: 'first_name' },
        { text: 'Last Name', value: 'last_name' },
        { text: 'Email', value: 'email_address' },
        { text: 'Form Email Consent', value: 'email_consent_agreed', align: 'center' },
        { text: 'Phone', value: 'endpoint' },
        { text: 'Form Phone Consent', value: 'phone_consent_agreed', align: 'center' },
        { text: 'Birthday', value: 'birthdate' },
        { text: 'Double Opt-In (DOI) State *', value: 'double_opt_in_state', align: 'center' },
        { text: 'DOI Completed Date', value: 'time_double_opt_in_complete_date' },
        { text: 'DOI Completed Time', value: 'time_double_opt_in_complete_time' },
      ],
      full_text: null,
      filter_state: 'all',
      selected_form_id: null,
      show_test_submissions: false,
      exporting: false,

      // Generate submissions dialog
      generate_submissions_dialog: false,
      generating_submissions: false,
      selected_forms_for_generation: [],
      submission_count: 10,
      seed: null,
      forms: [],

      // Validation rules
      rules: {
        required: value => !!value || 'Required.',
        submission_count_range: value =>
          (value >= 1 && value <= 499) || 'Must be between 1 and 499.',
      },
    };
  },

  computed: {
    should_enable_generate_submissions() {
      return (this.$rc.inDev() || this.$rc.inStaging()) && this.$rc.$state.isAuthActorSystemAdmin();
    },
    no_data_message() {
      if (this.full_text && this.full_text.trim()) {
        return `No submissions found matching "${this.full_text}"`;
      }
      return 'No submissions available';
    },

    form_options() {
      if (!this.forms) return [];
      return this.forms.map(form => ({
        text: form.internal_label || 'Untitled Form',
        value: form.guest_check_in_form_id,
      }));
    },

    organization_label() {
      return this.$rc.$state.getOrganizationLabel();
    },
  },

  watch: {
    filter_state() {
      this.doUpdate();
    },
    full_text() {
      this.update();
    },
  },

  mounted() {
    this.initializeFromQueryParams();
    this.doUpdate();
    this.loadForms();
  },

  methods: {
    update: _.debounce(function() {
      this.doUpdate();
    }, 600),

    doUpdate() {
      this.loading = true;

      const params = {
        full_text: this.full_text,
        organization_id: this.$route.params.organization_id,
        include_test_submissions: this.show_test_submissions,
      };

      if (this.selected_form_id) {
        params.guest_check_in_form_id = this.selected_form_id;
      }

      if (this.filter_state !== 'all') {
        params.state_filter = this.filter_state;
      }

      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/guest-check-in/submissions/search',
        params: params,
        on_success: data => {
          this.results = data.results || [];
          this.loading = false;
        },
        on_error: (error) => {
          this.loading = false;
          this.$rc.showError('Unable to list guest check-in submissions', error);
        },
      });
    },

    exportSubmissions() {
      this.exporting = true;
      const params = {
        organization_id: this.$route.params.organization_id,
        full_text: this.full_text,
        state_filter: this.filter_state === 'all' ? null : this.filter_state,
      };

      if (this.selected_form_id) {
        params.guest_check_in_form_id = this.selected_form_id;
      }

      this.$rc.excelExport('EXPORT: /api/v1/guest-check-in/submissions', params, () => {});
      this.exporting = false;
    },

    getStateColor(state) {
      switch (state) {
        case 'double-opt-in-pending':
          return 'orange';
        case 'opted-in':
          return 'green';
        case 'opted-out':
          return 'red';
        case 'double-opt-in-not-used':
          return 'grey';
        default:
          return 'grey';
      }
    },
    
    getStateDisplayText(state) {
      const map = {
        'double-opt-in-pending': 'Pending',
        'opted-in': 'Opted In',
        'opted-out': 'Opted Out',
        'double-opt-in-not-used': 'Not Used',
      };
      return map[state] || state;
    },

    loadForms() {
      this.loading = true;

      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/guest-check-in/forms/search',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        on_success: data => {
          this.forms = data.results || [];
          this.loading = false;
        },
        on_error: error => {
          this.$rc.showError('Failed to load guest check-in forms', error);
          this.loading = false;
        },
      });
    },

    showGenerateSubmissionsDialog() {
      this.generate_submissions_dialog = true;
    },

    generateTestSubmissions() {
      if (!this.selected_forms_for_generation.length || !this.submission_count) {
        return;
      }

      this.generating_submissions = true;

      const gen_params = {
        organization_id: this.$route.params.organization_id,
        guest_check_in_form_ids: this.selected_forms_for_generation,
        submission_count: this.submission_count,
      };
      if (this.seed !== null && this.seed !== undefined && this.seed !== '') {
        gen_params.seed = this.seed;
      }

      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/guest-check-in/generate-test-submissions',
        params: gen_params,
        on_success: data => {
          this.generating_submissions = false;
          this.generate_submissions_dialog = false;
          this.doUpdate(); // Refresh the list
        },
        on_error: error => {
          this.generating_submissions = false;
          this.$rc.error('Unable to generate test submissions: ' + error);
        },
      });
    },

    initializeFromQueryParams() {
      // Initialize form selection from query parameter
      if (this.$route.query.guest_check_in_form_id) {
        this.selected_form_id = this.$route.query.guest_check_in_form_id;
      }
    },

    clearFormFilter() {
      this.selected_form_id = null;
      this.update();
    },

    getSelectedFormLabel() {
      if (!this.selected_form_id || !this.forms) return '';
      const form = this.forms.find(f => f.guest_check_in_form_id === this.selected_form_id);
      return form ? form.internal_label : 'Unknown Form';
    },

    viewAllAnalytics() {
      this.$router.push({
        name: 'all-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewDemoGuestCheckInFlow() {
      const route = this.$router.resolve({
        name: 'search-forms-guest-check-in-demo-gateway',
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped></style>
